import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../hooks/useAppSettings'
import { formatRelativeTime as fmtRelative } from '../utils/time'
import PaywallModal from '../components/PaywallModal'
import { collection, query, where, orderBy, getDocs, getDoc, addDoc, doc, serverTimestamp } from 'firebase/firestore'
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage'
import { db, storage } from '../utils/firebase'
import {
  Plus,
  Search,
  MessageSquare,
  X,
  Loader,
  ImagePlus,
  Trash2
} from 'lucide-react'
import './DiscussionsPage.css'

// Fallback category list — used only when a neighborhood hasn't
// customized its own via Admin → Manage Categories.
const DEFAULT_CATEGORIES = [
  'General',
  'Maintenance',
  'Events',
  'Safety',
  'Governance',
  'Marketplace',
  'Tool Sharing',
  'Complaints'
]

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'mostVotes', label: 'Top voted' },
  { value: 'mostReplies', label: 'Most replies' }
]

function DiscussionsPage() {
  const { userProfile, isNeighborHidden, isSubscriptionActive } = useAuth()
  const isAdmin = userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
  const [discussions, setDiscussions] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('newest')
  const [filterCategory, setFilterCategory] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [neighborhoodName, setNeighborhoodName] = useState('')
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES)
  // email -> profilePhotoURL, built once from a users query so each
  // discussion card can render the author's real avatar instead of
  // falling back to an initial. Discussion docs store `authorId` as
  // the author's email (see rules), which is how we key.
  const [authorPhotos, setAuthorPhotos] = useState({})

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadDiscussions()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile?.neighborhoodId])

  const loadDiscussions = async () => {
    try {
      const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', userProfile.neighborhoodId))
      if (neighborhoodDoc.exists()) {
        const data = neighborhoodDoc.data()
        setNeighborhoodName(data.name || userProfile.neighborhoodId)
        if (Array.isArray(data.discussionCategories) && data.discussionCategories.length > 0) {
          setCategories(data.discussionCategories)
        } else {
          setCategories(DEFAULT_CATEGORIES)
        }
      }
    } catch (e) {
      console.error('Error loading neighborhood:', e)
    }

    try {
      setLoading(true)
      const discussionsQuery = query(
        collection(db, 'discussions'),
        where('neighborhoodId', '==', userProfile.neighborhoodId),
        orderBy('lastActivity', 'desc')
      )

      const snapshot = await getDocs(discussionsQuery)
      const discussionsList = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdDate: d.data().createdDate?.toDate() || new Date(),
        lastActivity: d.data().lastActivity?.toDate() || new Date()
      }))

      setDiscussions(discussionsList)

      // Build the author-photo lookup. Query the users in this
      // neighborhood once and store an { email → profilePhotoURL } map
      // so cards can render <img> instead of an initial. Failing this
      // lookup shouldn't break the page — cards just show initials.
      try {
        const usersSnap = await getDocs(query(
          collection(db, 'users'),
          where('neighborhoodId', '==', userProfile.neighborhoodId)
        ))
        const map = {}
        usersSnap.docs.forEach(u => {
          const data = u.data() || {}
          if (data.email && data.profilePhotoURL) {
            map[data.email] = data.profilePhotoURL
          }
        })
        setAuthorPhotos(map)
      } catch (e) {
        console.warn('Could not load author photos:', e)
      }
    } catch (error) {
      console.error('Error loading discussions:', error)
    } finally {
      setLoading(false)
    }
  }

  const filteredDiscussions = discussions
    .filter(d => !isNeighborHidden(d.authorId))
    .filter(d => {
      if (filterCategory && d.category !== filterCategory) return false
      if (searchQuery) {
        const search = searchQuery.toLowerCase()
        return d.title.toLowerCase().includes(search) ||
               d.content?.toLowerCase().includes(search)
      }
      return true
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return a.lastActivity - b.lastActivity
        case 'mostVotes':
          return ((b.upvotes || 0) - (b.downvotes || 0)) - ((a.upvotes || 0) - (a.downvotes || 0))
        case 'mostReplies':
          return (b.messageCount || 0) - (a.messageCount || 0)
        default:
          return b.lastActivity - a.lastActivity
      }
    })

  const { settings } = useAppSettings()
  const formatRelativeTime = (date) => fmtRelative(date, settings)

  // Category filter chip list. If the category list is short enough,
  // it renders as pill tabs; otherwise the extras collapse into a
  // "More" pill that opens a native select.
  const primaryCategories = categories.slice(0, 5)
  const overflowCategories = categories.slice(5)

  const discussionsCount = filteredDiscussions.length
  const totalCount = discussions.length
  const locationLabel = neighborhoodName || userProfile?.neighborhoodId || 'your neighborhood'

  return (
    <div className="discussions-page">
      {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}

      {/* Header */}
      <header className="discussions-header">
        <div className="discussions-header-text">
          <h1>Discussions</h1>
          <p className="muted">
            {totalCount === 0
              ? `No discussions in ${locationLabel} yet`
              : `${totalCount} ${totalCount === 1 ? 'discussion' : 'discussions'} in ${locationLabel}`}
          </p>
        </div>
        <button
          className="btn-primary discussions-new-btn"
          onClick={() => setShowCreateModal(true)}
          aria-label="New discussion"
          title="New discussion"
          data-walk="new-discussion-btn"
        >
          <Plus size={22} strokeWidth={2.5} />
        </button>
      </header>

      {/* Search */}
      <div className="discussions-search">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search discussions"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button
            type="button"
            className="discussions-search-clear"
            onClick={() => setSearchQuery('')}
            aria-label="Clear search"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Filters: category segmented + sort segmented */}
      <div className="discussions-filters">
        <div className="segmented" role="tablist" aria-label="Category">
          <button
            type="button"
            className={`segmented-item${filterCategory === '' ? ' active' : ''}`}
            onClick={() => setFilterCategory('')}
          >
            All
          </button>
          {primaryCategories.map(cat => (
            <button
              key={cat}
              type="button"
              className={`segmented-item${filterCategory === cat ? ' active' : ''}`}
              onClick={() => setFilterCategory(cat)}
            >
              {cat}
            </button>
          ))}
          {overflowCategories.length > 0 && (
            <div className="segmented-more">
              <select
                value={overflowCategories.includes(filterCategory) ? filterCategory : ''}
                onChange={(e) => setFilterCategory(e.target.value)}
                aria-label="More categories"
              >
                <option value="">More…</option>
                {overflowCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="segmented" role="tablist" aria-label="Sort">
          {SORT_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`segmented-item${sortBy === opt.value ? ' active' : ''}`}
              onClick={() => setSortBy(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Discussions List */}
      <div className="discussions-list">
        {loading ? (
          <div className="discussions-loading">
            <div className="spinner"></div>
            <p className="muted">Loading discussions…</p>
          </div>
        ) : discussionsCount === 0 ? (
          <div className="card discussions-empty">
            <div className="discussions-empty-icon">
              <MessageSquare size={32} />
            </div>
            <h3>No discussions yet</h3>
            <p className="muted">
              {searchQuery || filterCategory
                ? 'Try clearing filters or searching for something else.'
                : `Be the first to start a conversation in ${locationLabel}.`}
            </p>
            {!searchQuery && !filterCategory && (
              <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
                <Plus size={18} />
                <span>Start a discussion</span>
              </button>
            )}
          </div>
        ) : (
          filteredDiscussions.map((discussion, idx) => (
            <DiscussionCard
              key={discussion.id}
              discussion={discussion}
              formatRelativeTime={formatRelativeTime}
              photoURL={authorPhotos[discussion.authorId] || discussion.authorPhotoURL || ''}
              isFirst={idx === 0}
            />
          ))
        )}
      </div>

      {/* Floating Action Button (Mobile) */}
      <button
        className="fab"
        onClick={() => setShowCreateModal(true)}
        aria-label="New discussion"
      >
        <Plus size={24} />
      </button>

      {/* Create Discussion Modal */}
      {showCreateModal && (
        <CreateDiscussionModal
          categories={categories}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false)
            loadDiscussions()
          }}
          userProfile={userProfile}
        />
      )}
    </div>
  )
}

// Discussion Card — composed from `.card.interactive` primitive.
// Avatar-forward layout: circular avatar on the left (photo or
// initials), title + 2-line body preview in the middle, and a
// muted footer row with meta.
function DiscussionCard({ discussion, formatRelativeTime, photoURL, isFirst }) {
  const initial = (discussion.authorFirstName || 'A').charAt(0).toUpperCase()
  const messageCount = discussion.messageCount || 0
  const resolvedPhoto = photoURL || discussion.authorPhotoURL || ''

  return (
    <Link
      to={`/discussions/${discussion.id}`}
      className="discussion-card card interactive"
      {...(isFirst ? { 'data-walk': 'discussion-card-first' } : {})}
    >
      <div className="avatar" aria-hidden="true">
        {resolvedPhoto ? (
          <img src={resolvedPhoto} alt="" onError={(e) => { e.currentTarget.style.display = 'none' }} />
        ) : (
          initial
        )}
      </div>

      <div className="discussion-card-main">
        <div className="discussion-card-titlerow">
          <h3 className="discussion-card-title">{discussion.title}</h3>
          {discussion.category && (
            <span className="badge discussion-card-category">{discussion.category}</span>
          )}
        </div>

        {discussion.content && (
          <p className="discussion-card-preview">{discussion.content}</p>
        )}

        <div className="discussion-card-meta">
          <span className="discussion-card-author">
            {discussion.authorFirstName || 'Anonymous'}
            {discussion.authorStreetAddress && ` · ${discussion.authorStreetAddress}`}
          </span>
          <span className="discussion-card-dot" aria-hidden="true">·</span>
          <span>{formatRelativeTime(discussion.lastActivity)}</span>
          <span className="discussion-card-dot" aria-hidden="true">·</span>
          <span className="discussion-card-replies">
            <MessageSquare size={14} />
            {messageCount} {messageCount === 1 ? 'reply' : 'replies'}
          </span>
        </div>
      </div>

      {discussion.imageURL && (
        <div className="discussion-card-thumb">
          <img src={discussion.imageURL} alt="" />
        </div>
      )}
    </Link>
  )
}

// Create Discussion Modal — kept intact structurally (same fields,
// same submit path). Only visual polish tweaks.
function CreateDiscussionModal({ onClose, onCreated, userProfile, categories }) {
  const modalCategories = (Array.isArray(categories) && categories.length > 0)
    ? categories
    : DEFAULT_CATEGORIES
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [category, setCategory] = useState(modalCategories[0] || 'General')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    setError('')
  }

  const removeImage = () => {
    setImageFile(null)
    if (imagePreview) URL.revokeObjectURL(imagePreview)
    setImagePreview(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!title.trim()) {
      setError('Please enter a title')
      return
    }

    try {
      setLoading(true)
      setError('')

      let imageURL = ''

      if (imageFile) {
        const filename = `${Date.now()}_${imageFile.name}`
        const storageRef = ref(storage, `discussion_images/${filename}`)
        await uploadBytes(storageRef, imageFile)
        imageURL = await getDownloadURL(storageRef)
      }

      await addDoc(collection(db, 'discussions'), {
        title: title.trim(),
        content: content.trim(),
        category,
        authorId: userProfile.email,
        authorFirstName: userProfile.firstName,
        authorStreetAddress: userProfile.streetAddress,
        neighborhoodId: userProfile.neighborhoodId,
        createdDate: serverTimestamp(),
        lastActivity: serverTimestamp(),
        messageCount: 0,
        upvotes: 0,
        downvotes: 0,
        isPinned: false,
        isLocked: false,
        votedUsers: {},
        imageURL
      })

      onCreated()
    } catch (err) {
      console.error('Error creating discussion:', err)
      setError('Failed to create discussion. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>New discussion</h2>
          <button type="button" className="btn-ghost close-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="form-error">{error}</div>}

          <div className="form-group">
            <label htmlFor="title">Title</label>
            <input
              id="title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="What would you like to discuss?"
              required
              data-walk="discussion-title-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              data-walk="discussion-category-select"
            >
              {modalCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="content">Details (optional)</label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Add more details about your discussion…"
              rows={4}
              data-walk="discussion-content-textarea"
            />
          </div>

          <div className="form-group">
            <label>Photo (optional)</label>
            {imagePreview ? (
              <div className="image-preview-wrapper">
                <img src={imagePreview} alt="Preview" className="image-preview" />
                <button type="button" className="remove-image-btn" onClick={removeImage}>
                  <Trash2 size={16} />
                </button>
              </div>
            ) : (
              <label className="image-upload-area" htmlFor="image-upload">
                <ImagePlus size={22} />
                <span>Click to add a photo</span>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  hidden
                />
              </label>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading} data-walk="discussion-submit-btn">
              {loading ? (
                <>
                  <Loader size={18} className="spinner" />
                  {imageFile ? 'Uploading…' : 'Creating…'}
                </>
              ) : (
                'Create discussion'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DiscussionsPage
